import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ContentCopy, Close } from '@mui/icons-material';
import * as meetingService from '../../services/meetingService';

const ShareDialog = ({ open, onClose, meetingId, meetingTitle }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expirationDays, setExpirationDays] = useState(7);

  const handleGenerateLink = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await meetingService.createShareLink(meetingId, expirationDays);
      setShareData(response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (shareData?.shareUrl) {
      navigator.clipboard.writeText(shareData.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareData(null);
    setError('');
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Share Meeting
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Generate a shareable link for: <strong>{meetingTitle}</strong>
        </Typography>
        
        {!shareData ? (
          <Box mt={3}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Link Expiration</InputLabel>
              <Select
                value={expirationDays}
                onChange={(e) => setExpirationDays(e.target.value)}
                label="Link Expiration"
              >
                <MenuItem value={1}>1 day</MenuItem>
                <MenuItem value={3}>3 days</MenuItem>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
              </Select>
            </FormControl>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              This link will allow anyone with access to view the meeting details, summary, action items, and transcript. The link will expire after the selected period.
            </Alert>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box mt={3}>
            <TextField
              fullWidth
              label="Shareable Link"
              value={shareData.shareUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink} edge="end">
                      <ContentCopy />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            {copied && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Link copied to clipboard!
              </Alert>
            )}
            
            <Alert severity="info">
              This link will expire on{' '}
              {new Date(shareData.expiresAt).toLocaleString()}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {!shareData && (
          <Button
            variant="contained"
            onClick={handleGenerateLink}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Generating...' : 'Generate Link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
